import React, { useState, useEffect } from 'react';
import { countries, Country } from './countries';
import { ChevronDown, Check, X } from 'lucide-react';

type PhoneInputProps = {
    value: string;
    onChange: (value: string) => void;
    className?: string;
    id?: string;
};

const phonePatterns: Record<string, RegExp> = {
    FR: /^(\+33|0)[1-9]\d{8}$/, // France
    US: /^(\+1)?[2-9]\d{2}[2-9]\d{6}$/, // USA
    GB: /^(\+44|0)7\d{9}$/, // UK
    DE: /^(\+49|0)[1-9]\d{1,13}$/, // Germany
    ES: /^(\+34|0)[6-9]\d{8}$/, // Spain
    IT: /^(\+39|0)3\d{8,9}$/, // Italy
    BE: /^(\+32|0)[1-9]\d{7,8}$/, // Belgium
    CH: /^(\+41|0)[1-9]\d{8}$/, // Switzerland
    LU: /^(\+352|0)[1-9]\d{5,8}$/, // Luxembourg
};

export const PhoneInput = ({ value, onChange, className, id }: PhoneInputProps) => {
    const [countryCode, setCountryCode] = useState('FR');
    const [showDropdown, setShowDropdown] = useState(false);
    const [isValid, setIsValid] = useState(false);

    const selectedCountry = countries.find((c: Country) => c.code === countryCode) || countries[0];

    useEffect(() => {
        // Validate phone number when value or country changes
        const pattern = phonePatterns[countryCode];
        if (!value) {
            setIsValid(false);
            return;
        }

        // Test the value directly against the pattern
        setIsValid(pattern.test(value));
    }, [value, countryCode]);

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value.replace(/[^0-9+]/g, '');
        onChange(val);
    };

    return (
        <div className={`relative ${className}`}>
            <div className="flex items-center">
                {/* Country selector */}
                <button
                    type="button"
                    className="flex items-center gap-1 px-3 border-r border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 rounded-l-lg h-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-slate-700 dark:text-slate-200"
                    onClick={() => setShowDropdown(!showDropdown)}
                    aria-label="Choisir un pays"
                >
                    <span className={`fi fi-${countryCode.toLowerCase()}`}></span>
                    <ChevronDown className="w-4 h-4" />
                </button>

                {/* Phone input */}
                <div className="relative flex-1 min-w-[200px]">
                    <input
                        id={id}
                        type="tel"
                        value={value}
                        onChange={handlePhoneChange}
                        className="w-full px-4 py-2 border rounded-r-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        placeholder={selectedCountry.example}
                    />
                    {value && (
                        <span className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${isValid ? 'text-green-500' : 'text-red-500'}`}>
                            {isValid ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                        </span>
                    )}
                </div>
            </div>

            {/* Country dropdown */}
            {showDropdown && (
                <div className="absolute top-full left-0 z-10 mt-1 w-56 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-60 overflow-auto">

                    {countries.map((country: Country) => (
                        <div
                            key={country.code}
                            className="flex items-center px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer text-slate-700 dark:text-slate-200"
                            onClick={() => {
                                setCountryCode(country.code);
                                setShowDropdown(false);
                                onChange(country.dial_code + value.replace(/^\+?[0-9]*/, ''));
                            }}
                        >
                            <span className={`fi fi-${country.code.toLowerCase()} mr-2`}></span>
                            <span>{country.name} ({country.dial_code})</span>
                        </div>
                    ))}
                </div>
            )}

            {/* Validation message */}
            {value && !isValid && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                    Format invalide pour {selectedCountry.name}. Ex: {selectedCountry.example}
                </p>
            )}
        </div>
    );
};
